import {createSlice} from '@reduxjs/toolkit';
import {ApiState, createInitialApiState, setApiPending, setApiRejected, setApiSuccess} from '../apiState';
import {abandonSignup, pollSignupStatus, resendSignupLink, startSignup} from './signupThunks';
import {markPendingSignupPaid, readPendingSignup} from '../../lib/signupSession';
import {apiErrorMessage, isApiError} from '../../lib/apiError';
import type {SignupStatus} from '../../backend-types/signup-api';

export interface SignupData {
    /** The token from `/signup/start`. Null until the visitor submits the form. */
    token: string | null;
    email: string;
    name: string;
    expiresAt: string | null;
    /** Paddle reported `checkout.completed`. The status endpoint cannot tell us this. */
    paid: boolean;
    /** Last answer from `/signup/status`. Null before the first poll. */
    status: SignupStatus | null;
    loginLinkSentAt: string | null;
    /** 409 from `/signup/start` — the address already has an account, so offer sign-in. */
    accountExists: boolean;
    /** From a throttled resend (429). Counted down by the screen. */
    resendRetryAfterSeconds: number | null;
}

interface SignupState {
    api: {
        start: ApiState;
        status: ApiState;
        resend: ApiState;
    };
    data: SignupData;
}

/**
 * Hydrated from `localStorage` at import time so a reload mid-signup resumes instead of dropping
 * someone who has already paid back onto an empty form. `readPendingSignup` drops anything expired,
 * so this can only ever restore a signup that is still redeemable.
 */
function initialData(): SignupData {
  const pending = readPendingSignup();

  return {
    token: pending?.token ?? null,
    email: pending?.email ?? '',
    name: pending?.name ?? '',
    expiresAt: pending?.expiresAt ?? null,
    paid: pending?.paid ?? false,
    status: null,
    loginLinkSentAt: null,
    accountExists: false,
    resendRetryAfterSeconds: null,
  };
}

const initialState: SignupState = {
  api: {
    start: createInitialApiState(),
    status: createInitialApiState(),
    resend: createInitialApiState(),
  },
  data: initialData(),
};

const signupSlice = createSlice({
  name: 'signup',
  initialState,
  reducers: {
    /**
     * Paddle says the card went through, which ends the flow: the screen switches straight to
     * "check your email" and the status poll carries on behind it, only to decide whether a resend
     * is offerable. Nobody is asked to watch it.
     */
    checkoutCompleted: (state) => {
      state.data.paid = true;
      markPendingSignupPaid();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(startSignup.pending, (state) => {
      state.data.accountExists = false;
      setApiPending(state.api.start);
    });
    builder.addCase(startSignup.fulfilled, (state, action) => {
      state.data.token = action.payload.token;
      state.data.email = action.payload.email;
      state.data.name = action.payload.name;
      state.data.expiresAt = action.payload.expiresAt;
      state.data.paid = false;
      state.data.status = null;
      setApiSuccess(state.api.start);
    });
    builder.addCase(startSignup.rejected, (state, action) => {
      // `account_exists` is not really an error the form should shout about — the screen turns it
      // into a link to sign in — but the message is still stored so nothing is silently swallowed.
      state.data.accountExists = isApiError(action.payload) && action.payload.code === 'account_exists';
      setApiRejected(state.api.start, apiErrorMessage(action.payload, 'Could not start signup'));
    });

    builder.addCase(pollSignupStatus.pending, (state) => {
      setApiPending(state.api.status);
    });
    builder.addCase(pollSignupStatus.fulfilled, (state, action) => {
      state.data.status = action.payload.status;
      state.data.email = action.payload.email;
      state.data.loginLinkSentAt = action.payload.loginLinkSentAt;
      state.data.expiresAt = action.payload.expiresAt;
      setApiSuccess(state.api.status);
    });
    builder.addCase(pollSignupStatus.rejected, (state, action) => {
      // `unknown_token` is terminal — the server has no record of this signup, so there is nothing
      // left to wait for. Treated as expired so the screen offers a fresh start instead of spinning.
      if (isApiError(action.payload) && action.payload.code === 'unknown_token') {
        state.data.status = 'expired';
      }
      setApiRejected(state.api.status, apiErrorMessage(action.payload, 'Could not check your signup'));
    });

    builder.addCase(resendSignupLink.pending, (state) => {
      state.data.resendRetryAfterSeconds = null;
      setApiPending(state.api.resend);
    });
    builder.addCase(resendSignupLink.fulfilled, (state) => {
      setApiSuccess(state.api.resend);
    });
    builder.addCase(resendSignupLink.rejected, (state, action) => {
      state.data.resendRetryAfterSeconds = isApiError(action.payload)
        ? action.payload.retryAfterSeconds ?? null
        : null;
      setApiRejected(state.api.resend, apiErrorMessage(action.payload, 'Could not resend the link'));
    });

    builder.addCase(abandonSignup.fulfilled, (state) => {
      state.api = {
        start: createInitialApiState(),
        status: createInitialApiState(),
        resend: createInitialApiState(),
      };
      // The email and name are kept on purpose: starting over almost always means correcting one
      // field, not retyping both.
      state.data = {
        ...state.data,
        token: null,
        expiresAt: null,
        paid: false,
        status: null,
        loginLinkSentAt: null,
        accountExists: false,
        resendRetryAfterSeconds: null,
      };
    });
  },
});

export const {checkoutCompleted} = signupSlice.actions;
export default signupSlice.reducer;
